import { mdiPlus, mdiRefresh } from "@mdi/js";
import Icon from "@mdi/react";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash";
import moment from "moment";
import LoadingWidget from "../../../context_providers/modal/loader";
import {
  useToast,
  ToastType,
} from "../../../context_providers/toast/toast_ctx";
import { fetchGet, toQueryString } from "../../../service/api_client";
import { urls } from "../../../utils/urls";
import { useAuth } from "../../../context_providers/auth/authProvider";
import { ACTION, MODULE } from "../../../utils/enums";
import {
  Department,
  DepartmentFilterData,
} from "../models/department_model";

const defaultFilter: DepartmentFilterData = {
  page: 1,
  count: 50,
};

const DepartmentListScreen: FunctionComponent = () => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/org/department`;
  const navigation = useHistory();
  const { showToast } = useToast();
  const { isAuthorised } = useAuth();

  const [filter, setFilter] = useState<DepartmentFilterData>(defaultFilter);
  const [data, setData] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [canEdit, setCanEdit] = useState(false);

  useEffect(() => {
    setCanEdit(
      isAuthorised({ module: MODULE.eaf, action: ACTION.CREATE }) ||
        isAuthorised({ module: MODULE.eaf, action: ACTION.UPDATE })
    );
  }, [isAuthorised]);

  const fetchData = async (f: DepartmentFilterData) => {
    setLoading(true);
    try {
      const res = await fetchGet(url + toQueryString(f));
      if (res.success) {
        setData(res.data.data || []);
        setTotal(res.data.metadata?.total || 0);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error: any) {
      showToast({ type: ToastType.error, text: error.message });
    }
    setLoading(false);
  };

  const debouncedFetch = useCallback(
    debounce((f: DepartmentFilterData) => fetchData(f), 400),
    []
  );

  useEffect(() => {
    debouncedFetch(filter);
  }, [filter]);

  return (
    <>
      <div className="flex justify-between items-end my-1">
        <h1 className="flex text-xl font-bold border-l-4 border-myPrimaryColor mx-5 pl-2">
          Departments
        </h1>
        <div className="flex gap-2 mx-5">
          <button
            onClick={() => fetchData(filter)}
            className="rounded-md border border-gray-300 bg-white text-sm px-3 py-1.5 flex items-center gap-1"
            title="Refresh"
          >
            <Icon path={mdiRefresh} size={0.7} />
            Refresh
          </button>
          {canEdit && (
            <button
              onClick={() => navigation.push(urls.department + "/create")}
              className="bg-myPrimaryColor text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1"
            >
              <Icon path={mdiPlus} size={0.7} />
              New Department
            </button>
          )}
        </div>
      </div>

      <div className="px-5 my-3">
        <input
          type="text"
          placeholder="Search by name, code, or ID..."
          className="border rounded-md px-3 py-1.5 w-full md:w-1/3 text-sm"
          onChange={(e) =>
            setFilter((f) => ({ ...f, search: e.target.value, page: 1 }))
          }
        />
      </div>

      <div className="mx-5 mb-5 bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">ID</th>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Code</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Created</th>
              <th className="text-left px-3 py-2 font-semibold">Created by</th>
              <th className="text-center px-3 py-2 font-semibold">Dashboard</th>
              <th className="text-right px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="text-center py-6 text-gray-500">
                  No Departments found.
                </td>
              </tr>
            )}
            {data.map((dep) => (
              <tr key={dep.id} className="border-t hover:bg-gray-50">
                <td className="px-3 py-2">{dep.id}</td>
                <td className="px-3 py-2 font-medium">{dep.name}</td>
                <td className="px-3 py-2">{dep.code}</td>
                <td className="px-3 py-2">
                  <span
                    className={
                      "inline-block px-2 py-0.5 rounded text-xs font-semibold " +
                      (dep.status === "Active"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-200 text-gray-600")
                    }
                  >
                    {dep.status || "—"}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-500">
                  {dep.created_at
                    ? moment(dep.created_at).format("DD-MMM-YYYY")
                    : "—"}
                </td>
                <td className="px-3 py-2 text-gray-500">
                  {dep.created_by_name || "—"}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    onClick={() =>
                      navigation.push(urls.dept_dashboard + "/" + dep.id)
                    }
                    className="text-xs border border-myPrimaryColor text-myPrimaryColor rounded-md px-2 py-0.5 hover:bg-myPrimaryColor hover:text-white"
                  >
                    View
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  {canEdit && (
                    <button
                      onClick={() =>
                        navigation.push(urls.department + "/update/" + dep.id)
                      }
                      className="text-myPrimaryColor hover:underline text-sm"
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-5 pb-10 flex justify-between items-center text-sm text-gray-600">
        <span>
          Showing {data.length} of {total}
        </span>
        <div className="flex gap-2">
          <button
            disabled={filter.page <= 1}
            onClick={() => setFilter((f) => ({ ...f, page: f.page - 1 }))}
            className="px-3 py-1 rounded border bg-white disabled:opacity-50"
          >
            Prev
          </button>
          <span className="px-2 py-1">Page {filter.page}</span>
          <button
            disabled={data.length < filter.count}
            onClick={() => setFilter((f) => ({ ...f, page: f.page + 1 }))}
            className="px-3 py-1 rounded border bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {loading && <LoadingWidget />}
    </>
  );
};

export default DepartmentListScreen;
